import { useMenu } from "hook/useMenu";
import { ManagementWrapper, RadioItem } from "../manager/ManagementStyled";
import React, { useMemo } from "react";
import { MdUpdate, MdUpload } from "react-icons/md";
import { RiDownload2Fill } from "react-icons/ri";
import { COLORS, LEVELS } from "styles/colors";
import { IoIosArrowForward } from "react-icons/io";
import { useFlag } from "@unleash/proxy-client-react";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { LEVEL_TYPE } from "api/interfaces/commonInterface.interface";
import { useAppSelector } from "redux/hooks";
import { useQuery } from "react-query";
import { getFirmwareUpgrade } from "api/manageAPI";
import { IoIosUnlock } from "react-icons/io";
import { RoutesProp } from "api/interfaces/menuInterface.interface";
import { ReactComponent as RecorderSVG } from "assets/icons/sidenav/recorder.svg";
interface MenuItem extends RoutesProp {
  iconColor: string;
  subTitle: string;
  icon: React.ReactNode;
}

export default function MaintenancePage() {
  const { menuNavigation, currentMenu } = useMenu({});
  const isFirmwareUpgradeEnable = useFlag("feature_manage_firmwareUpgrade");
  const isCovaConfigEnable = useFlag("feature_report_inventory_cova_config");
  const isStagingRecorderEnable = useFlag("feature_maintenance_staging");
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const { data: firmwareUpgradeData } = useQuery(
    ["firmwareUpgradeDefaultData", selectedAccount.accountId],
    () => getFirmwareUpgrade(selectedAccount.accountId),
    {
      retry: 0,
      enabled:
        !!selectedAccount.accountId &&
        isFirmwareUpgradeEnable &&
        selectedAccount.accountLevel === LEVEL_TYPE.EU,
      refetchOnWindowFocus: false,
    }
  );

  const menus: MenuItem[] = useMemo(() => {
    if (currentMenu?.children) {
      return currentMenu.children
        .map((item) =>
          getItem(item, selectedAccount, {
            isFirmwareUpgradeEnable,
            isCovaConfigEnable,
            isStagingRecorderEnable,
          })
        )
        .filter((item): item is MenuItem => item !== null);
    }
    return [];
  }, [
    currentMenu?.children,
    isFirmwareUpgradeEnable,
    isCovaConfigEnable,
    selectedAccount,
  ]);

  return (
    <ManagementWrapper>
      {menus.map((item) => (
        <RadioItem
          aria-disabled={false}
          isDisabled={false}
          checked={false}
          $iconColor={item.iconColor}
          onClick={() => menuNavigation({ to: item.routeName as string })}
          key={item.name}
          data-testid="maintenance-maintenance-page-tsx-maintenance-radio-item-menu-navigation"
        >
          <span className="radio-item-checked-icon">{item.icon}</span>
          <div className="report-item-content">
            <div className="report-item-title">
              <div className="report-radio-item">
                {item.name}
                {isFirmwareUpgradeEnable &&
                  firmwareUpgradeData?.result &&
                  item.routeName === "/maintenance/firmware" && (
                    <span
                      className={`firmware-upgrade-status ${
                        firmwareUpgradeData.result.isEnabled ? "on" : "off"
                      }`}
                    >
                      {firmwareUpgradeData.result.isEnabled ? "On" : "Off"}
                    </span>
                  )}
              </div>
            </div>
            <div className="subtitle">{item.subTitle}</div>
            {/* <div className="item-link">
             START {item.title} <IoArrowForward />
            </div> */}
            <IoIosArrowForward className="arrow-icon" />
          </div>
        </RadioItem>
      ))}
    </ManagementWrapper>
  );
}
const getItem = (
  item: RoutesProp,
  selectedAccount: ProfileAccountInfo,
  unleashFlags: {
    isFirmwareUpgradeEnable: boolean;
    isCovaConfigEnable: boolean;
    isStagingRecorderEnable: boolean;
  }
): MenuItem | null => {
  if (item.routeName === "/maintenance/firmware") {
    if (!unleashFlags.isFirmwareUpgradeEnable) {
      return null;
    }
    return {
      ...item,
      name:
        selectedAccount.accountLevel === LEVEL_TYPE.EU
          ? "COVA Firmware Auto-Update"
          : "COVA Firmware Package",
      icon:
        selectedAccount.accountLevel === LEVEL_TYPE.EU ? (
          <MdUpdate color={COLORS.WHITE} />
        ) : (
          <MdUpload color={COLORS.WHITE} />
        ),

      subTitle:
        selectedAccount.accountLevel === LEVEL_TYPE.EU
          ? "Your myDW automatically updates whenever new firmware is released."
          : "Upload a COVA firmware package to your myDW.",
      iconColor: LEVELS.EU,
    };
  }
  if (item.routeName === "/maintenance/configuration") {
    if (!unleashFlags.isCovaConfigEnable) {
      return null;
    }
    return {
      ...item,
      icon: <RiDownload2Fill color={COLORS.WHITE} />,
      subTitle: "Back up and save your current COVA settings.",
      iconColor: LEVELS.CP,
    };
  }
  if (item.routeName === "/maintenance/adminUnlock") {
    return {
      ...item,
      icon: <IoIosUnlock color={COLORS.WHITE} />,
      subTitle: "Generate a temporary Admin password for VMAX, Encoder, COVA.",
      iconColor: "#448ed2",
    };
  }
  if (item.routeName === "/maintenance/staging") {
    if (
      selectedAccount.accountLevel !== LEVEL_TYPE.CP ||
      !unleashFlags.isStagingRecorderEnable
    ) {
      return null;
    }
    return {
      ...item,
      icon: <RecorderSVG color={COLORS.WHITE} className="recorder-icon" />,
      subTitle:
        "Set up and configure recorders in your organization first, then easily transfer ownership to your End-User when ready.",
      iconColor: LEVELS.EU,
    };
  }
  return null;
};
